import { useI18n } from '@ecom/i18n';
import { type ReactElement, useEffect, useRef, useState } from 'react';

import { useUploadProductImageMutation } from '../api';
import type { ProductImage } from '../types';

import { ImageGallery, type GalleryEntry } from './ImageGallery';

import { belowRecommendedParams } from '@/lib/image-size';
import { uploadErrorMessage } from '@/lib/upload-error';

/**
 * Images Produits lot — this component owns only the TRANSIENT upload
 * state: a local `URL.createObjectURL(file)` preview while a file is in
 * flight, and an inline error if it fails. The REAL, persisted gallery
 * (any product status, any browser session) is `ProductImageGallery`,
 * rendered alongside this uploader and invalidated automatically on a
 * successful upload (`useUploadProductImageMutation`) — so a finished
 * entry is dropped from here rather than kept, to avoid showing the same
 * image twice. Before this lot, no read path existed anywhere in the API
 * for a previously-uploaded image, so this component could only ever show
 * a local preview of the file just picked; that gap is what this lot
 * closes end to end.
 */
export function ImageUploader({
  productId,
  onUploaded,
}: {
  productId: string;
  onUploaded: (image: ProductImage) => void;
}): ReactElement {
  const { t } = useI18n();
  const uploadMutation = useUploadProductImageMutation(productId);
  const [entries, setEntries] = useState<readonly GalleryEntry[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  // One line per accepted picture that is under the recommended size (stored, but it may look soft).
  const [advisories, setAdvisories] = useState<readonly string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Tracks the latest `entries` for the unmount-only cleanup below, without
  // making that effect re-run (and re-revoke every existing preview) on
  // every new upload — an empty dependency array reading a stale `entries`
  // closure would revoke none of the URLs added after the first render;
  // `[entries]` as a dependency would revoke ALL of them on every new file.
  // Neither is what "revoke on unmount" means.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  useEffect(() => {
    // Object URLs are revoked on unmount — they hold a reference to the
    // in-memory blob for as long as the browser keeps them alive otherwise.
    return () => {
      for (const entry of entriesRef.current) URL.revokeObjectURL(entry.previewUrl);
    };
  }, []);

  function handleFiles(files: FileList | null): void {
    if (files === null) return;
    setAdvisories([]);
    for (const file of Array.from(files)) {
      const localId = `${file.name}-${String(Date.now())}-${String(Math.random())}`;
      const previewUrl = URL.createObjectURL(file);
      setEntries((current) => [
        ...current,
        { localId, previewUrl, fileName: file.name, status: 'uploading' },
      ]);

      uploadMutation.mutate(
        { file },
        {
          onSuccess: (result) => {
            // Dropped, not marked "done": `useUploadProductImageMutation`
            // already invalidates the real, persisted gallery
            // (`ProductImageGallery`, rendered alongside this uploader),
            // which will show this exact image within one refetch — keeping
            // a second, local-only copy here would just duplicate it.
            URL.revokeObjectURL(previewUrl);
            setEntries((current) => current.filter((entry) => entry.localId !== localId));
            onUploaded(result);
            const params = belowRecommendedParams(file.name, result.size);
            if (params !== null) {
              setAdvisories((current) => [
                ...current,
                t('merchant.images.belowRecommended', params),
              ]);
            }
          },
          onError: (error) => {
            setEntries((current) =>
              current.map((entry) =>
                entry.localId === localId
                  ? {
                      ...entry,
                      status: 'error',
                      errorMessage: uploadErrorMessage(error, t),
                    }
                  : entry,
              ),
            );
          },
        },
      );
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') inputRef.current?.click();
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => {
          setIsDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          handleFiles(event.dataTransfer.files);
        }}
        className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors ${
          isDragging ? 'border-accent bg-accentSoft' : 'border-line bg-ground'
        }`}
      >
        <p className="text-sm font-medium text-ink">{t('merchant.products.images.dropzone')}</p>
        <p className="text-xs text-inkFaint">{t('merchant.products.images.formats')}</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(event) => {
            handleFiles(event.target.files);
            event.target.value = '';
          }}
        />
      </div>

      {advisories.length > 0 ? (
        <ul role="status" className="flex flex-col gap-1 text-xs text-warn">
          {advisories.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
      ) : null}

      <ImageGallery entries={entries} />
    </div>
  );
}
